import React, { useState } from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import SEO from './SEO';
import { 
  CheckCircle,
  BarChart3,
  Users,
  Clock,
  Globe,
  MessageCircle,
  TrendingUp,
  Calendar,
  ArrowRight,
  Zap,
  Shield,
  Phone,
  PieChart,
  Activity,
  Star,
  Briefcase,
  Building,
  Headphones,
  Target,
  BookOpen,
  ChevronRight,
  Github,
  Twitter,
  Linkedin,
  Youtube
} from 'lucide-react';

const FeaturesPage = () => {
  const [selectedMetric, setSelectedMetric] = useState('sentiment');

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  // Comprehensive features data organized by category
  const featuresData = {
    "Voice Recognition & Understanding": [
      {
        icon: Headphones,
        title: "Advanced Natural Language Processing",
        description: "State-of-the-art voice understanding that captures context, intent, and nuance in employee questions.",
        benefits: ["99.2% accuracy rate", "Handles complex queries", "Multi-accent support"]
      },
      {
        icon: Globe,
        title: "Multi-Language Voice Support",
        description: "Seamlessly handles queries in multiple languages for diverse global workforces.",
        benefits: ["25+ languages supported", "Real-time translation", "Cultural context awareness"]
      },
      {
        icon: MessageCircle,
        title: "Contextual Response Generation",
        description: "Delivers precise, contextually relevant answers drawn from your specific company knowledge base.",
        benefits: ["Company-specific responses", "Policy-compliant answers", "Real-time updates"]
      }
    ],
    "Knowledge Management & Integration": [
      {
        icon: BookOpen,
        title: "Smart Document Processing",
        description: "Automatically ingests and organizes HR policies, SOPs, and internal documentation.",
        benefits: ["PDF/Word integration", "Auto-categorization", "Version control"]
      },
      {
        icon: Target,
        title: "Real-Time Knowledge Updates",
        description: "Instantly reflects policy changes and updates across all employee interactions.",
        benefits: ["Live sync capability", "Zero downtime updates", "Consistency guaranteed"]
      },
      {
        icon: Shield,
        title: "Secure Knowledge Architecture",
        description: "Enterprise-grade security ensures sensitive company information remains protected.",
        benefits: ["Role-based access", "Encryption at rest", "Audit trails"]
      }
    ],
    "Analytics & Performance": [
      {
        icon: BarChart3,
        title: "Comprehensive Usage Analytics",
        description: "Detailed insights into employee query patterns, knowledge gaps, and system performance.",
        benefits: ["Real-time dashboards", "Custom reporting", "Predictive insights"]
      },
      {
        icon: Activity,
        title: "Performance Optimization",
        description: "Continuous learning algorithms improve response accuracy and speed over time.",
        benefits: ["Machine learning", "Response optimization", "Performance tracking"]
      },
      {
        icon: TrendingUp,
        title: "ROI Measurement & Reporting",
        description: "Track time savings, productivity gains, and operational efficiency improvements.",
        benefits: ["Cost savings tracking", "Productivity metrics", "Executive reporting"]
      }
    ],
    "Enterprise Integration": [
      {
        icon: Building,
        title: "Seamless System Integration",
        description: "Connects with existing HR systems, intranets, and enterprise software platforms.",
        benefits: ["API-first design", "SSO integration", "Custom connectors"]
      },
      {
        icon: Users,
        title: "Team Collaboration Features",
        description: "Enables knowledge sharing and collaborative content management across departments.",
        benefits: ["Cross-team sharing", "Content collaboration", "Knowledge verification"]
      },
      {
        icon: Zap,
        title: "Instant Deployment",
        description: "Quick setup and implementation with minimal IT involvement required.",
        benefits: ["Cloud-based setup", "Minimal configuration", "Fast onboarding"]
      }
    ]
  };

  // Analytics dashboard mock data
  const analyticsData = {
    sentiment: {
      title: "Customer Sentiment Analysis",
      data: [
        { label: "Positive", value: 68, color: "#22c55e" },
        { label: "Neutral", value: 25, color: "#64748b" },
        { label: "Negative", value: 7, color: "#ef4444" }
      ]
    },
    volume: {
      title: "Call Volume Analysis - Hourly Breakdown", 
      data: [
        { label: "9-11 AM", value: 28, color: "#3b82f6" },
        { label: "11-1 PM", value: 45, color: "#8b5cf6" },
        { label: "1-3 PM", value: 52, color: "#06b6d4" },
        { label: "3-5 PM", value: 38, color: "#10b981" }
      ]
    },
    distribution: {
      title: "Department Call Distribution",
      data: [
        { label: "HR", value: 35, color: "#f59e0b" },
        { label: "IT Support", value: 28, color: "#ef4444" },
        { label: "Finance", value: 22, color: "#8b5cf6" },
        { label: "Operations", value: 15, color: "#06b6d4" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEO 
        title="VioQbase Features - Voice-Powered AI Knowledge Base for Enterprise Teams"
        description="Discover VioQbase's powerful features: advanced voice recognition, intelligent knowledge management, real-time analytics, and seamless enterprise integration for maximum productivity."
        keywords="voice AI features, enterprise knowledge base, voice recognition, AI analytics, HR automation, knowledge management"
        canonical="https://vioqbase.com/features"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center max-w-5xl mx-auto space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight">
              Powerful Features for <span className="text-[#ff7a1f]">Enterprise</span> Knowledge Management
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Discover how VioQbase transforms workplace efficiency with advanced voice AI, intelligent knowledge processing, and comprehensive analytics that deliver measurable results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button 
                size="lg" 
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                onClick={handleDemo}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book a Demo
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#112d56] font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300"
              >
                <ArrowRight className="mr-2 h-5 w-5" />
                Try VioQbase Calculator
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          {Object.entries(featuresData).map(([category, features], categoryIndex) => (
            <div key={category} className={`${categoryIndex > 0 ? 'mt-20' : ''}`}>
              <div className="text-center mb-16">
                <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                  {category}
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-white to-gray-50">
                      <CardHeader className="space-y-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center group-hover:from-[#ff7a1f]/30 group-hover:to-[#ff7a1f]/10 transition-all duration-300">
                          <IconComponent className="h-8 w-8 text-[#ff7a1f]" />
                        </div>
                        <CardTitle className="text-xl font-bold text-[#112d56] group-hover:text-[#ff7a1f] transition-colors duration-300">
                          {feature.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                        <div className="space-y-2">
                          {feature.benefits.map((benefit, benefitIndex) => (
                            <div key={benefitIndex} className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Analytics Dashboard Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Real-Time Analytics Dashboard
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Get comprehensive insights into your voice AI performance with detailed analytics and actionable intelligence.
            </p>
          </div>

          {/* Metric Selection */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {Object.entries(analyticsData).map(([key, metric]) => (
              <Button
                key={key}
                variant={selectedMetric === key ? "default" : "outline"}
                className={`${selectedMetric === key 
                  ? 'bg-[#ff7a1f] hover:bg-[#e6681b] text-white' 
                  : 'border-[#112d56] text-[#112d56] hover:bg-[#112d56] hover:text-white'
                } font-semibold px-6 py-3 rounded-xl transition-all duration-300`}
                onClick={() => setSelectedMetric(key)}
              >
                {metric.title}
              </Button>
            ))}
          </div>

          {/* Analytics Visualization */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white p-6">
              <h3 className="text-2xl font-bold mb-3">{analyticsData[selectedMetric].title}</h3>
              <p className="text-blue-200">Real-time performance insights and data visualization</p>
            </div>
            
            <div className="p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {analyticsData[selectedMetric].data.map((item, index) => (
                  <Card key={index} className="text-center border-0 shadow-md hover:shadow-lg transition-all duration-300">
                    <CardHeader className="pb-2">
                      <div 
                        className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white font-bold text-2xl"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.value}%
                      </div>
                      <CardTitle className="text-lg font-bold text-gray-800">{item.label}</CardTitle>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#112d56] text-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
              Ready to Transform Your Workplace with <span className="text-[#ff7a1f]">Voice AI</span>?
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              See how VioQbase can eliminate delays, reduce support tickets, and boost your team's productivity with intelligent voice-powered knowledge access.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button 
                size="lg" 
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                onClick={handleDemo}
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Your Demo
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#112d56] font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300"
              >
                <ArrowRight className="mr-2 h-5 w-5" />
                Try VioQbase Calculator
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-8 h-8 bg-[#ff7a1f] rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
                <span className="text-2xl font-bold">VioQbase</span>
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern business.
              </p>
              <div className="flex space-x-4">
                <a href="https://github.com/vioqbase" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <Github className="h-6 w-6" />
                </a>
                <a href="https://twitter.com/vioqbase" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <Twitter className="h-6 w-6" />
                </a>
                <a href="https://linkedin.com/company/vioqbase" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <Linkedin className="h-6 w-6" />
                </a>
                <a href="https://www.youtube.com/channel/UCij66Z3ffQtVIw3aIqNVN0g" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-[#ff7a1f] transition-colors">
                  <Youtube className="h-6 w-6" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="/products/vioconcierge" className="hover:text-[#ff7a1f] transition-colors text-lg">VioConcierge</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioqbase-demo" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;